<template>
  <div v-if="!hideChatApp">
    <slot></slot>
    <Nudging
      :show="showNudging && !webView"
      :retention="retention"
      :mobile="$mqAliases.isMobile" />
    <FabChat
      :show="showFab"
      :mobile="$mqAliases.isMobile"
      :unreadMessages="unreadMessages"
      :retention="retention"
      @fabClick="onFabClick"
    >
    </FabChat>
    <transition name="slide-fade">
      <div
        v-if="chatIsOpen"
        class="chat-window"
        :class="{
          [$mqAliases.isMobile || webView || getFullScreen ? 'full' : 'popup']: true,
          'retention': retention,
          'header-exists': !webView,
          'customer': isCustomerFlow,
          'rating-header': !isCustomerFlow && conversationState === 'CONVERSATION_RATING'
        }"
      >
        <ChatHeader
          :mobile="$mqAliases.isMobile"
          :showNewMessageAlert="showNewMessageAlert"
          @scrollToEnd="scrollToEnd"
        />
        <AppIsDown v-if="appIsDown"/>
        <div
          v-else
          :class="webView ? 'h-100-web-view' : (isArchiveView ? 'h-100-small' : 'h-100')"
        >
          <ChatContainer
            :retention="retention"
            @scrollToEnd= "scrollToEnd"
            @hideLoader="showLoader = false"
            @showNewMessageAlert="showNewMessageAlert = true"
            @reset="reset"/>
          <div
            v-if="conversationState === 'CONVERSATION_SUSPENDED'"
            class="transcript-enduser"
          >
            <Transcript mode="enduser" />
          </div>
          <div
            v-if="['CONVERSATION_ENDED', 'CONVERSATION_TRANSCRIPT'].includes(conversationState)"
            class="transcript-endAgent"
          >
            <Transcript />
          </div>
          <Rating
            v-if="(
              conversationState === 'CONVERSATION_RATING' &&
              !isCustomerFlow
            )"
          />
          <EndWithTNPS
            v-if="isTNPSActive && (
              ['CONVERSATION_RATING', 'CONVERSATION_NEW']
                .includes(conversationState) &&
              isCustomerFlow
            )"
            @close="reset"
            @open="openChat"
          />
          <NpsRating
            v-if="!isTNPSActive && (conversationState === 'CONVERSATION_RATING') &&
            isCustomerFlow"
          />
          <End
            v-if="(isTNPSActive ? !isCustomerFlow : true) && (
              conversationState === 'CONVERSATION_RATED' ||
              conversationState === 'CONVERSATION_NEW'
            )"
            @close="reset"
            @open="openChat"
          />
        </div>
      </div>
    </transition>
    <SurveyModal v-if="showTNPSModal" @close="setShowTNPSModal(false)"/>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import npm from '../package.json'
import { isDeactivated, dispatchLatestUnreadMessage } from '@/helpers/helpers'
import Transcript from '@/components/Transcript'
import Rating from '@/components/Rating'
import NpsRating from '@/components/NpsRating'
import EndWithTNPS from '@/components/EndWithTNPS'
import End from '@/components/End'
import FabChat from '@/components/FabChat'
import Nudging from '@/components/Nudging'
import ChatHeader from '@/components/ChatHeader'
import ChatContainer from '@/components/ChatContainer'
import AppIsDown from '@/components/AppIsDown'
import SurveyModal from '@/components/survey/SurveyModal'
import { trigger, triggerType } from '@/helpers/Tracker'
import { EVENT, listenToCustomEvent } from '@/helpers/customEvents'

export default {
  data () {
    return {
      showLoader: true,
      clipped: false,
      drawer: true,
      fixed: false,
      showNewMessageAlert: true,
      miniVariant: false,
      right: true,
      rightDrawer: false,
      title: 'Vuetify.js',
      retention: false,
      timeoutNudging: null,
      hideChatApp: false,
      appIsDown: false,
      isTNPSActive: process.env.VUE_APP_TNPS_ACTIVE
    }
  },
  name: 'SanitasChat',
  components: {
    ChatContainer,
    ChatHeader,
    Rating,
    NpsRating,
    EndWithTNPS,
    Transcript,
    End,
    Nudging,
    FabChat,
    AppIsDown,
    SurveyModal
  },

  created () {
    console.info(
      '%c ⚑ sani%ctas%c Chat Enduser',
      'font-weight:bold;color:#00765A;',
      'font-weight:bold;color:#5BAC26;',
      'font-weight:bold;color:#AAAAAA;',
      `v${npm.version}`,
    )

    if (isDeactivated(window._sanChatConfig)) {
      this.appIsDown = true
      return
    }

    window._sanChatConfig = {
      ...window._sanChatConfig,
      customerFlow: window._sanChatConfig?.isCustomerFlow
    }

    this.initChatFab(window._sanChatConfig)

    listenToCustomEvent(EVENT.LISTEN_TO.TRIGGER_NUDGING, (e) => {
      this.enableNudgingByEvent()
      if (e.detail && e.detail.specialCase === 'retention') {
        this.retention = true
        this.setTrigger('RETENTION')
      }
    })

    listenToCustomEvent(EVENT.LISTEN_TO.DESTROY, () => {
      this.setNudgingByEvent(null)
      this.retention = false
      this.$store.dispatch('conversation/reset')
      this.hideChatApp = true
    })

    listenToCustomEvent(EVENT.LISTEN_TO.SHOW_FAB, () => {
      this.hideChatApp = false
      this.$store.dispatch('uistate/close')
    })

    listenToCustomEvent(EVENT.LISTEN_TO.SET_LANGUAGE, (e) => {
      if (e.detail && e.detail.language) {
        this.$store.dispatch('uistate/updateUILanguage', {lang: e.detail.language})
      }
    })
  },

  mounted () {
    if (isDeactivated(window._sanChatConfig)) {
      return
    }

    if (this.isCustomerFlow && this.webView) {
      this.$store.dispatch('uistate/open')
          .then(() => {
            setTimeout(() => {
              this.scrollToEnd()
              this.resetUnreadMessages()
            })
          })
          .then(() => {
            if (!this.conversation) {
              this.$store.dispatch('conversation/init', {})
            }
          })
    } else {
      window.addEventListener('resize', this.scrollToEnd)
      this.fixScrollFab(this.showFab)
      setInterval(() => { this.fixScrollFab(this.showFab) }, 3000)

      // Nudging Logic
      this.initNudging(window._sanChatConfig)
    }

    listenToCustomEvent(EVENT.LISTEN_TO.CHECK_UNREAD_MESSAGES, () => {
      dispatchLatestUnreadMessage(this.getUnreadMessages, this.$store.getters['uistate/chatIsOpen'])
    })
  },

  methods: {
    ...mapActions('conversation', [
      'resetUnreadMessages',
      'setTrigger'
    ]),
    ...mapActions('uistate', [
      'setChatFab',
      'setCustomerFlow',
      'checkAgentAvailable',
      'setShowTNPSModal'
    ]),
    ...mapActions('nudging', [
      'setNudging',
      'setNudgingDelay',
      'closeNudgeConv',
      'incrementNudgingShowCounter',
      'setNudgingByEvent'
    ]),

    initNudging ({ nudging, nudgingDelay }) {
      if (nudgingDelay > 0 && nudgingDelay < 1000) {
        nudgingDelay = nudgingDelay * 1000
      }
      this.setNudgingDelay(nudgingDelay)
      this.timeoutNudging = setTimeout(() => {
        if (this.nudging === null) {
          this.checkAgentAvailable()
            .then(() => this.setNudging(nudging))
        } else if (this.nudging === true) {
          this.checkAgentAvailable()
        }
      }, this.nudgingDelay)
    },
    initChatFab (config) {
      this.setChatFab(config)
      this.setCustomerFlow(config)
    },
    fixScrollFab (value) {
      // if there is a back-to-top element, move it.
      let backtotop = document.getElementsByTagName('sanitas-scroll-to-top')
      if (backtotop.length > 0) {
        backtotop = backtotop[0]
        let scrollbtn
        if (backtotop.querySelector) scrollbtn = backtotop.querySelector('#scroll-top-button')
        if (!scrollbtn && backtotop.shadowRoot && backtotop.shadowRoot.querySelector) scrollbtn = backtotop.shadowRoot.querySelector('#scroll-top-button')
        if (scrollbtn) {
          if (value) {
            scrollbtn.style.bottom = '155px'
            scrollbtn.style.right = '22px'
          } else {
            scrollbtn.style.bottom = ''
            scrollbtn.style.right = ''
          }
        }
      }
    },
    onFabClick () {
      if (!this.chatIsOpen) {
        trigger(triggerType.ACTION, 'ChatFabClicked')
        this.$store.dispatch('uistate/open')
          .then(() => {
            setTimeout(() => {
              this.scrollToEnd()
              this.resetUnreadMessages()
            })
          })
      } else {
        trigger(triggerType.ACTION, 'ChatFabClicked')
        this.$store.dispatch('uistate/close')
      }
    },
    reset () {
      this.setNudgingByEvent(null)
      this.retention = false
      this.$store.dispatch('uistate/close')
      this.$store.dispatch('conversation/reset')
      if (this.webView) {
        this.onFabClick()
      }
    },
    openChat () {
      this.$store.dispatch('uistate/open')
      if (this.webView) {
        this.onFabClick()
      }
    },
    scrollToEnd () {
      const container = this.$el.querySelector('.chatContainer .content')
      if (!container) {
        return
      }
      container.scrollTop = container.scrollHeight
    },
    enableNudgingByEvent () {
      if (this.showNudgingByEvent !== null) {
        this.checkAgentAvailable().then(({isAgentAvailable, isOpeningHours}) => {
          if (isOpeningHours && isAgentAvailable &&
            (!this.conversation || this.conversation.convId < -1)) {
            this.setNudgingByEvent(true)
          }
        })
      }
    }
  },

  computed: {
    ...mapState('uistate', ['conversationState', 'behavior', 'showTNPSModal']),
    ...mapGetters('uistate', ['isArchiveView', 'chatFabHidden', 'chatIsOpen', 'chatAvailable', 'chatFabHidden', 'webView', 'isCustomerFlow', 'getFullScreen']),
    ...mapState('conversation', ['conversation', 'unreadMessages', 'isActiveConversation', 'acceptedTerms']),
    ...mapGetters('conversation', ['getUnreadMessages']),
    ...mapState('nudging', ['nudging', 'nudgingDelay', 'showNudgingByEvent']),
    showFab () {
      return !this.chatIsOpen && ((!this.chatFabHidden && !this.isActiveConversation) || this.showNudgingByEvent)
    },
    showNudging () {
      return ((this.nudging &&
        !this.chatFabHidden &&
        this.chatAvailable &&
        !this.acceptedTerms) || this.showNudgingByEvent) && !this.chatIsOpen
    }
  },

  watch: {
    showFab (value) {
      this.fixScrollFab(value)
    },
    showNudging (value) {
      if (value) {
        trigger(triggerType.ACTION, 'ChatNudgingShown')
        this.incrementNudgingShowCounter()
      }
    },
    chatIsOpen (value) {
      if (value) {
        this.setNudgingByEvent(false)
        if (this.timeoutNudging !== null) {
          clearTimeout(this.timeoutNudging)
        }
      }
      if (this.nudging && value) {
        this.closeNudgeConv()
      }
    }
  }
}
</script>

<style lang="postcss">
  /* volunteer leaking to block body when chat is open on mobile mode */
  .chat-open {
    position: fixed;

    @media (--sm) {
      position: inherit;
    }
  }

  /* Globals (retrieved from aem global css) */
  .sanchat-wrapper {
    display: block !important;

    *, :after, :before {
      box-sizing: border-box;
      font-family: 'Open Sans',sans-serif;
    }

    p {
      margin: 0;
      font-weight: 400;
      font-style: normal;
      font-size: 15px;
      line-height: 1.35;
      letter-spacing: .25px;
      color: #666564;
    }
  }

  /* prevent the IOS devices to zoom when focusing a text field */
  @media screen and (-webkit-min-device-pixel-ratio:0) {
    select:focus,
    textarea:focus,
    input:focus {
      font-size: 16px;
    }
  }
  @media screen and (-webkit-min-device-pixel-ratio:0) {
    select,
    textarea,
    input {
      font-size: 16px;
    }
  }
</style>

<style scoped lang="postcss">
  .application--wrap, div >>> .application--wrap {
    min-height: 0;
  }

  a, div >>> a {
    color: var(--tw-c-original-green) !important;
  }

  /** ------------------ buttons styling ------------------------------------ */

  .minimize {
    letter-spacing: -2px;

    .btn__content, >>> .btn__content {
      position: relative;
      top: -3px;
    }
  }

  .btn, div >>> .btn {
    flex: inherit !important;
    text-transform: inherit;
  }

  .sanitas-button, div >>> .sanitas-button {
    border-radius: 28px;
    box-shadow: initial !important;
    font-weight: bold;
    font-size: 13px;
    height: auto;
    min-height: auto;

    &.sanitas-primary[disabled],
    &.sanitas-tertiary[disabled] {
      color: #dddddd !important;
      background-color: inherit !important;
      border-color: #dddddd;
    }

    &.sanitas-primary {
      background-color: var(--tw-c-original-green) !important;
      color: white;
    }

    &.sanitas-tertiary {
      border: 1px solid var(--tw-c-original-green) !important;
      color: var(--tw-c-original-green) !important;
      background-color: white !important;
    }

    &.gray {
      border: 1px solid var(--tw-c-cool-gray);
      color: var(--tw-c-cool-gray);
      background-color: white !important;
      fill: var(--tw-c-cool-gray);
    }
  }

  >>> nav {
    .sanitas-button {
      &.sanitas-tertiary:hover {
        border: 1px solid white !important;
        color: white !important;
        background-color: var(--tw-c-original-green) !important;
      }
    }
  }


  /** ---------------- / buttons styling ------------------------------------ */

  .chat-window {
    background-color: #f3f2f0;
    font-family: 'Open Sans', sans-serif;
    box-shadow: 0px 6px 6px -3px rgba(0,0,0,0.2), 0px 10px 14px 1px rgba(0,0,0,0.14), 0px 4px 18px 3px rgba(0,0,0,0.12);

    &.popup {
      position: fixed;
      width: 364px;
      height: calc(100vh - 264px);
      right: 60px;
      bottom: 0;
      z-index: 920;

      &.retention {
        z-index: 1001;
      }
    }

    @media (max-height: 870px) {
      &.popup {
        height: calc(100vh - 204px);
        width: 325px;
      }
    }

    .chatContainer {
      &.header-exists.full {
        height: calc(100% - 48px);
      }
    }

    &.full {
      position:fixed;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: var(--tw-z-index-10);
    }

    font-size: 14px;
    line-height: 1.5;
    color: black;

    >>> p, >>> div {
      text-rendering: optimizeLegibility;
      -webkit-font-smoothing: antialiased;
    }

    >>> .btn.btn--disabled:not(.btn--icon):not(.btn--flat),
    >>> .btn.btn--disabled:not(.btn--icon):not(.btn--flat) {
      background-color: rgba(0,0,0,0.12) !important;
    }

    .chat-container {
      border-radius: inherit;
      position: absolute;
      left: -365px;
      top: 0px;
      background-color: #f3f2f0;

      .chat-layout {
        border-radius: inherit;
        width: 100%;
      }
    }

    .z-conversation-container {
      overflow: auto;
      outline: 1px solid #dddddd;
      height: 100%;
      min-height: 10px;
      flex: 1 2 auto;
    }

    .isQuestionAnswered {
      overflow: hidden !important;
    }

    .conversation-container {
      overflow: auto;
      outline: 1px solid #dddddd;

      height: calc(100vh - 48px - 116px - 60px);
      height: -moz-calc(100vh - 48px - 116px - 60px);
      max-height: inherit;

      /* handle the Safari toolbar issue */
      @supports (-webkit-overflow-scrolling: touch) {
        height: calc(100vh - 48px - 116px - 60px - 84px);
      }

      @media(--md) {
        /* these height and max-height are related to the 'chat-window' height calculation */
        /* 100px is an acceptable space between for an nice display */
        /* 48px is first toolbar height (fixed) */
        /* 116px is second toolbar height (fixed) */
        /* 60px is typer height (fixed, see below) */
        height: calc(100vh - 100px - 48px - 116px - 60px);
        max-height: calc(600px - 48px - 116px - 60px);
        height: -moz-calc(100vh - 100px - 48px - 116px - 60px);
        max-height: -moz-calc(600px - 48px - 116px - 60px);
      }
    }
  }

  .first-toolbar {
    background-color: var(--tw-c-original-green);
    height: 48px;
    border-radius: inherit;
    flex: none;
    margin-top: 0!important;
  }

  .second-toolbar {
    position: relative;
    z-index: 2;
    box-shadow:none;
    border-bottom:1px solid #dddddd !important;
    flex: none;
    margin-top: 0!important;
  }

  .new-message-alert {
    cursor: pointer;
    position: absolute;
    background-color: var(--tw-c-aubergine);
    color: var(--tw-c-white);
    padding: 10px;
    top: calc(48px + 116px);
    width: 100%;
    z-index: 2;
  }

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    .avatar-container {
      .avatar {
        left: 110px;
      }
    }
  }

  .avatar-container {
    height: 116px;
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;

    .avatar {
      position: absolute;
      top: -38px;
      border: 1px solid var(--tw-c-cool-gray) !important;
      overflow: hidden;

      img {
        background-color: white !important;
      }
    }

    .avatar-legend {
      position: relative;
      top: 65px;
      font-family: 'Open Sans', sans-serif;
      font-size: 13px;
      font-weight: bold;
      color: var(--tw-c-medium-gray);
    }

    .consultant {
      position: relative;
      top: 65px;
      font-family: 'Open Sans', sans-serif;
      font-size: 13px;
      color: var(--tw-c-medium-gray);
    }
  }

  .toolbar-element {
    flex: 1;
    align-self: flex-start;

    &.close-button {
      margin-right: 2px !important;
      text-align: right;
    }

    &.finish-button {
      margin-left: 2px !important;
    }
  }

  .transcript, .rating {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 2;
  }

  .terms-container {
    background-color: #3c3a38;
    padding: 8px;
  }
  .terms {
    overflow: auto;
    overflow-x: hidden;
    height:130px;
    font-size: 12px;
    color: white;

    h3 {
      font-family: 'Open Sans', sans-serif;
      font-size: 14px;
      font-weight: bold;
      color: var(--tw-c-original-green);
      margin-bottom: 8px;
    }
    >>> p {
      font-size: 12px;
      color: white;
      margin-bottom: 8px;
    }
    >>> a {
      font-size: 12px;
      word-break: break-all;
    }
  }
  .fade-enter-active, .fade-leave-active {
    transition: opacity .05s;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  .loadingContainer {
    height: calc(100% - 158px);
  }
  .slide-fade-enter-active {
    transition: all .3s ease;
  }
  .slide-fade-leave-active {
    transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }
  .slide-fade-enter, .slide-fade-leave-to {
    transform: translateX(20px);
    opacity: 0;
  }
  .h-100 {
    height: calc(100% - 99px);
  }
  .h-100-web-view {
    height: 100%;
  }
  .h-100-small {
    height: calc(100% - 55px);
  }
</style>

